import { engineComponent, NumberUtils } from "neo-common";
import { Communication } from "neo-games";
import { ComponentTypes } from "../Components/ComponentTypes";
import { WinAnimator } from "../GameComponents/WinAnimator";
import { ContainerElement } from "../Components/Elements/Graphics/ContainerElement";
import { Symbols } from "../GameComponents/Symbols";
import { SwitchElement } from "../Components/Elements/Graphics/SwitchElement";
import { BaseView } from "../GameComponents/Base/BaseView";
import { FreespinsView } from "../GameComponents/Freespins/FreespinsView";
import { PlayList } from "../Components/Sounds/PlayList";
import { ModuleManager } from "../Managers/ModuleManager";
import { Payline } from "../GameComponents/Payline";
import { Symbol } from "../GameComponents/Symbol";
import { PaylinesExtended } from "./PaylinesExtended";
import { Settings } from "./Settings";
import { CreateFrameTimer, FrameTimerEventEmitter, FrameTimerEvents } from "../Utils/FrameTimer";
import { BetManager } from "../Managers/BetManager";
import { ReelExtended } from "./ReelExtended";
import { SymbolExtended } from "./SymbolExtended";
import { ImageTextCounterElement } from "../Components/Elements/Texts/ImageTextCounterElement";
import { ImageElement } from "../Components/Elements/Graphics/ImageElement";
import { Tweens } from "../Components/Effects/Tweens";
import { AppContext } from "../../Common/AppContext";
import { Scaling } from "../Utils/Scaling";

@engineComponent(ComponentTypes.DisplayObject)
export class WinAnimatorExtended extends WinAnimator {
    private _finishFirstWinsDelay: FrameTimerEventEmitter;

    // ActiveElements
    private _activeElementsNames: Array<string> = [];
    private _activeElements: Array<SwitchElement> = [];
    private _activeElementsCase: string;
    protected static readonly CaseWin = "WIN";
    protected static readonly CaseHide = "HIDE";

    public static Force: boolean = false;
    // protected _scatterFsAnim: SwitchElement;

    protected _scattertCount: number = 0;
    public _wasHighWin: boolean = false;
    protected _logo: SwitchElement;
    protected _logIdleCounter: number = 0;
    private _reelBg: SwitchElement;
    private _frame: SwitchElement;
    private _paylines: PaylinesExtended;

    private _idleLogoDelay: number = 240;
    private _idleMaxValue: number = 800;
    protected timerLogoStatus: string;
    protected timerLogo: FrameTimerEventEmitter;
    protected static readonly IdleLogo: string = "idleLogo";

    private counter: ImageTextCounterElement;
    private counterGlow: ImageElement;
    private currentCounterStep: number;
    private counterBaseSize: number = 0.75;
    private counterGlowBaseSize: number = 1;
    private counterGlowMaxAlpha: number = 1;
    public isWild: boolean = false;

    private static readonly ShowFirstWinsStep1: string = "showFirstWinsStep1";

    private static readonly ShowFirstWinsStep2: string = "showFirstWinsStep2";

    private static readonly ShowFirstWinsStep3: string = "showFirstWinsStep3";

    private static readonly ShowFirstWinsStep4: string = "showFirstWinsStep4";

    private static readonly ShowFirstWinsStepFS: string = "showFirstWinsStepFS";

    private static readonly ShowFirstWinsStep5: string = "showFirstWinsStep5";

    protected showFirstWinsStep4Delay: FrameTimerEventEmitter;

    configure(node: Element): void {
        super.configure(node);

        if (node.hasAttribute(WinAnimatorExtendedAttributes.ACTIVE_ELEMENTS)) {
            this._activeElementsNames = node.getAttribute(WinAnimatorExtendedAttributes.ACTIVE_ELEMENTS).split(";");
        }

        this._finishFirstWinsDelay = CreateFrameTimer("finishFirstWinsDelay");
        this._finishFirstWinsDelay.on(FrameTimerEvents.Complete, this.finishFirstWinsDelay.bind(this));

        this.showFirstWinsStep4Delay = CreateFrameTimer("showFirstWinsStep4");
        this.showFirstWinsStep4Delay.on(FrameTimerEvents.Complete, this.showFirstWinsStep4.bind(this));
    }

    initialize(
        symbols: Symbols,
        paylines?: PaylinesExtended,
        noWinSymbolsContainer?: ContainerElement,
        symbolsContainer?: ContainerElement,
        parent?: BaseView | FreespinsView,
    ): void {
        super.initialize(symbols, paylines, noWinSymbolsContainer, symbolsContainer);

        this._paylines = paylines;
        if (parent) {
            this._reelBg = parent.ReelBg;
            this._frame = parent.ReelFrame;
            this._activeElementsNames.forEach((elementName) => {
                if (parent.childs[elementName] && parent.childs[elementName] instanceof SwitchElement) {
                    this._activeElements.push(parent.childs[elementName]);
                }
            });

            if (parent.childs["Logo"]) {
                this._logo = parent.childs["Logo"];
                this.createLogoTimer();
            }
        }
    }

    //******************************************************************************************************************
    //  ANIMATIONS
    //******************************************************************************************************************

    protected createAnimations(): void {
        super.createAnimations();

        this.counter = this.childs["WinCounter"];
        this.counterGlow = this.childs["Flare"];

        this.counter.scale.x = 0.6;
        this.counter.scale.y = 0.6;
        if (this.counter) {
            this.counterBaseSize = this.counter.scale.x;
        }
        if (this.counterGlow) {
            this.counterGlowBaseSize = this.counterGlow.scale.x;

            this.counterGlow.alpha = 0;
            this.counterGlow.scale.x = 0.2;
            this.counterGlow.scale.y = 0.2;
        }

        this.animationsContainer.addChild(this.counterGlow);
        this.animationsContainer.addChild(this.counter);
    }

    //******************************************************************************************************************
    //  TIMER
    //******************************************************************************************************************

    protected createLogoTimer(): void {
        this.timerLogoStatus = "";

        this.timerLogo = CreateFrameTimer("timerLogo");
        this.timerLogo.on(FrameTimerEvents.Complete, this.onTimerLogoComplete.bind(this));
    }

    protected startLogoTimer(status: string, duration: number): void {
        this.timerLogoStatus = status;

        this.timerLogo.start(duration);
    }

    protected stopLogoTimer(): void {
        if (this.timerLogo != null) {
            this.timerLogo.stop();
        }
    }

    public resetLogoTimer(): void {
        this.stopLogoTimer();
    }

    protected onTimerLogoComplete(): void {
        this.timerLogo.stop();

        switch (this.timerLogoStatus) {
            case WinAnimatorExtended.IdleLogo:
                this.idleLogo();
                break;
        }
    }

    public IsWin(): boolean {
        if (this.currentWins != null && this.currentWins.length > 0) {
            return true;
        } else if (this.currentWins != null && this.currentWins.length == 0) {
            return false;
        }
        return false;
    }

    public winSymbolsGoToCase(winIndex: number, caseType: string, useValue: boolean): Array<Symbol> {
        const win = this.currentWins[winIndex];
        let symbols: Array<Symbol> = [];

        symbols = super.winSymbolsGoToCase(winIndex, caseType, useValue);

        let currentCaseReelBg = this._reelBg.getCurrentCase();
        let currentCaseFrame = this._frame.getCurrentCase();

        if (win.count > 0 && caseType == "WIN" && !this.IsScatterWin(symbols)) {
            // this._reelBg.goToCase("TO_WIN");
            // this._frame.goToCase("TO_WIN");
            let isPortrait = Scaling.isPortrait();
            if (isPortrait) {
                this._wasHighWin = this.currentMultiplierIndex >= 3;
                if (!this._wasHighWin) {
                    if (currentCaseReelBg != "WIN" && currentCaseReelBg != "TO_WIN") {
                        this._reelBg.goToCase("WIN");
                    }
                    if (currentCaseFrame != "WIN" && currentCaseFrame != "TO_WIN") {
                        this._frame.goToCase("WIN");
                    }
                }
            } else {
                if (currentCaseReelBg != "WIN" && currentCaseReelBg != "TO_WIN") {
                    this._reelBg.goToCase("TO_WIN");
                }
                if (currentCaseFrame != "WIN" && currentCaseFrame != "TO_WIN") {
                    this._frame.goToCase("TO_WIN");
                }
            }
        }

        if (win.count > 0 && caseType == "CYCLE" && !this.IsScatterWin(symbols)) {
            let isPortrait = Scaling.isPortrait();
            if (isPortrait) {
                //if (!this._wasHighWin)
                {
                    if (currentCaseReelBg != "WIN" && currentCaseReelBg != "TO_WIN") {
                        this._reelBg.goToCase("WIN");
                    }
                    if (currentCaseFrame != "WIN" && currentCaseFrame != "TO_WIN") {
                        this._frame.goToCase("WIN");
                    }
                }
            }
        }

        //problem s linkami
        // else if(caseType!="CYCLE")
        // {
        //     this._paylines.goToCase("NORMAL");
        // }
        return symbols;
    }

    private IsScatterWin(symbols: Array<Symbol>): boolean {
        let countScatter = 0;
        for (let i = 0; i < symbols.length; i++) {
            if (symbols[i].id + 1 === Settings.SymbolBonus) {
                countScatter++;
            }
        }
        return countScatter >= 3;
    }

    public showInitWins(wins: Array<Communication.Game.IWin>, win: number): void {
        this.currentWins = wins;
        this.currentWin = win;

        this.resetAnimations();

        if (wins != null && wins.length > 0) {
            this.showCustomInitWin();
            this.showFirstInitWins();
            this.finishFirstInitWins();
        } else {
            this.startTimer(WinAnimator.Idle, this.getInitialIdleDelay());
            this.startLogoTimer(WinAnimatorExtended.IdleLogo, this._idleLogoDelay);
        }
    }

    public showWins(wins: Array<Communication.Game.IWin>, win: number): boolean {
        this.currentWins = wins;
        this.currentWin = win;

        this.resetCounter();
        this.resetAnimations();

        if (wins != null && wins.length > 0) {
            this.currentMultiplierIndex = 0;

            for (let i = 0; i < this.multipliers.length; i++) {
                if (this.currentWin / BetManager.Bet >= this.multipliers[i]) {
                    this.currentMultiplierIndex = i;
                }
            }

            if (!this.showCustomWin()) {
                this.showFirstWins();
            }

            return this.currentMultiplierIndex >= 2;
        } else {
            this.startTimer(WinAnimator.Idle, this.getInitialIdleDelay());
            this.startLogoTimer(WinAnimatorExtended.IdleLogo, this._idleLogoDelay);
            return false;
        }
    }

    protected finishFirstWins(): void {
        if (ModuleManager.freespinsEnabled()) {
            this._logo.goToCase("PLAY");
            // PlayList.play('SND_FSMessageStart');

            for (let i = 0; i < this.reelCount; i++) {
                for (let j = 0; j < this.symbolCount; j++) {
                    if (this.currentSymbols[i][j].id === Settings.SymbolBonus - 1) {
                        this.currentSymbols[i][j].goToCase("WIN");
                    }
                }
            }

            //this._finishFirstWinsDelay.start(10);
            this._finishFirstWinsDelay.start(60);
        } else {
            this.emit(WinAnimator.FinishWinAnimator);
            this.currentCycle = 0;
            this.cycleWins(true);
        }
    }

    finishFirstWinsDelay(): void {
        PlayList.play("SND_FSMessageStart");
        this.emit(WinAnimator.FinishWinAnimator);
    }

    protected cycleWins(isFirst: boolean = false) {
        if (this.currentWins == null) {
            return;
        }

        if (isFirst) {
            PlayList.setVolume("SND_BaseLoop", 0);
            this.cycleStep = 0;
        } else {
            this.cycleStep++;
        }

        PlayList.play("SND_BaseWinline");

        if (!ModuleManager.freespinsEnabled() && !ModuleManager.freespinsActive() && this.cycleStep % 8 === 0) {
            this.cycleStep = 0;
            PlayList.loop("SND_BaseRecapLoop", 0, true);
        }

        for (let i = 0; i < this.currentWins.length; i++) {
            this.winSymbolsGoToCase(i, Symbol.CaseNormal, false);
        }
        this.allSymbolsGoToCase("", true);
        this.allPaylinesGoToCase(Payline.CaseNowin);

        for (let i = 0; i < this.currentWins.length; i++) {
            this.winPaylineGoToCase(i, Payline.CaseNormal);
        }

        if (this.currentWins[this.currentCycle].type == Communication.Game.WinType.freeSpins) {
            this.currentCycle++;
        }

        if (this.currentCycle < this.currentWins.length) {
            this.winSymbolsGoToCase(this.currentCycle, Symbol.CaseCycle, true);
            this.winPaylineGoToCase(this.currentCycle, Payline.CaseCycle);
        }

        this.currentCycle = (this.currentCycle + 1) % this.currentWins.length;

        this.startTimer(WinAnimator.CycleWins, this.getCycleWinsDelayWithCorrection(isFirst));
    }

    protected getCycleWinsDelay(): number {
        return 130;
    }

    public onReelStopped(reelsData: Array<Communication.Game.IReel>, reelIndex: number): void {
        this.currentReels = reelsData;

        super.onReelStopped(reelsData, reelIndex);

        for (let j = 0; j < this.currentSymbols[reelIndex].length; j++) {
            const symbol = this.currentSymbols[reelIndex][j];
            if (symbol !== null && symbol.id + 1 == Settings.SymbolBonus) {
                if (
                    reelIndex < 3 ||
                    (reelIndex === 3 && this._scattertCount >= 1) ||
                    (reelIndex === 4 && this._scattertCount > 2)
                ) {
                    symbol.goToCase(SymbolExtended.CaseImpact);
                    PlayList.play("SND_BaseBonusHit0" + (this._scattertCount + 1));
                }
                this._scattertCount++;
            }
            if (symbol !== null && symbol.id + 1 == Settings.SymbolWild) {
                symbol.goToCase(SymbolExtended.CaseImpact);
            }
        }

        if (ReelExtended.Tease) {
            let branchState = BaseView.Branches.getCurrentCase();
            if (BaseView.Branches.getCurrentCase() != "START_SHOW" && BaseView.Branches.getCurrentCase() != "SHOW") {
                BaseView.Branches.goToCase("START_SHOW");
            }
            this.setActiveElementCase(WinAnimatorExtended.CaseWin);
            for (let i = 0; i < this.currentSymbols.length - 1; i++) {
                for (let j = 0; j < this.currentSymbols[i].length; j++) {
                    const symbol = this.currentSymbols[i][j];
                    if (symbol.id + 1 != Settings.SymbolTeasing) {
                        symbol.goToCase(Symbol.CaseNowin);
                    }
                }
            }

            // for (let j = 0; j < this.currentSymbols[reelIndex].length; j++) {
            //     const symbol = this.currentSymbols[reelIndex][j];
            //     if (symbol.id + 1 != Settings.SymbolTeasing) {
            //         symbol.goToCase(Symbol.CaseNowin);
            //     }
            // }

            if (reelIndex == this.reelCount - 1) {
                // stop teasing
                BaseView.Branches.goToCase("HIDE_TEASING");
                if (this._scattertCount < 3) {
                    // nowin after machine stop
                    if (this.getActiveElementCase() === WinAnimatorExtended.CaseWin) {
                        this.setActiveElementCase(WinAnimatorExtended.CaseHide);
                    }
                    for (let i = 0; i < this.currentSymbols.length - 1; i++) {
                        for (let j = 0; j < this.currentSymbols[i].length; j++) {
                            const symbol = this.currentSymbols[i][j];
                            if (symbol.id + 1 != Settings.SymbolTeasing) {
                                symbol.goToCase(Symbol.CaseNormal);
                            } else {
                                if (symbol.getCurrentCase() !== Symbol.CaseImpact) {
                                    symbol.goToCase(Symbol.CaseNormal);
                                }
                            }
                        }
                    }
                }
            }
        }

        this.sortSymbols();
    }

    // protected showFirstWins(): void {
    //     super.showFirstWins();
    //
    //     if(this.currentWins.length === 1 && this.currentWins[0].trigger === Communication.Game.WinTrigger.Scatter)
    //     {
    //
    //     } else {
    //         this.setActiveElementCase(WinAnimatorExtended.CaseWin);
    //     }
    // }

    protected onTimerComplete(): void {
        this.timer.stop();

        switch (this.timerStatus) {
            case WinAnimator.Idle:
                this.idle();
                break;
            case WinAnimator.CycleWins:
                this.cycleWins();
                break;
            case WinAnimatorExtended.ShowFirstWinsStep1:
                this.showFirstWinsStep1();
                break;
            case WinAnimatorExtended.ShowFirstWinsStep2:
                this.showFirstWinsStep2();
                break;
            case WinAnimatorExtended.ShowFirstWinsStep3:
                this.showFirstWinsStep3();
                break;
            case WinAnimatorExtended.ShowFirstWinsStep4:
                this.showFirstWinsStep4();
                break;
            case WinAnimatorExtended.ShowFirstWinsStepFS:
                this.showFirstWinsStepFS();
                break;
            case WinAnimatorExtended.ShowFirstWinsStep5:
                this.showFirstWinsStep5();
                break;
        }
    }

    // protected showFirstWins(): void {
    //     super.showFirstWins();
    //
    //     if(this.currentWins.length === 1 && this.currentWins[0].trigger === Communication.Game.WinTrigger.Scatter)
    //     {
    //
    //     } else {
    //         this.setActiveElementCase(WinAnimatorExtended.CaseWin);
    //     }
    // }

    protected showFirstWins(): void {
        if (this.currentMultiplierIndex >= 3) {
            // let isPortrait = Scaling.isPortrait()
            // if (isPortrait)
            // {
            //     this._frame.goToCase("NORMAL");
            // }

            this.allSymbolsGoToCase(Symbol.CaseNowin, true);
            this.allPaylinesGoToCase(Payline.CaseNowin);

            for (let i = 0; i < this.currentWins.length; i++) {
                this.winSymbolsGoToCase(i, Symbol.CaseWin, false);
                this.winPaylineGoToCase(i, Payline.CaseNormal);
            }

            this.startAnimation();
        } else if (
            this.currentWins.length === 1 &&
            this.currentWins[0].trigger === Communication.Game.WinTrigger.Scatter
        ) {
            this.allSymbolsGoToCase(Symbol.CaseNowin, true);
            this.startAnimation();
        } else {
            this.setActiveElementCase(WinAnimatorExtended.CaseWin);
            this.showFirstWinsStep1();
        }
    }

    private showFirstWinsStep1(): void {
        this.currentCounterStep = 0;

        this.allSymbolsGoToCase(Symbol.CaseNowin, true);

        for (let i = 0; i < this.currentWins.length; i++) {
            this.winSymbolsGoToCase(i, SymbolExtended.CaseNormal, false);
        }

        this.showFirstWinsStep2();
    }

    private showFirstWinsStep2(): void {
        this.allPaylinesGoToCase(Payline.CaseNowin);

        this.allSymbolsGoToCase("", true); // if case is empty, only move to container and no set new case (skip case)

        for (let i = 0; i < this.currentWins.length; i++) {
            this.winPaylineGoToCase(i, Payline.CaseNormal);
        }

        this.winPaylineGoToCase(this.currentCounterStep, Payline.CaseWin);
        this.winSymbolsGoToCase(this.currentCounterStep, Symbol.CaseNormal, false); // for reset symbol before WIN
        this.winSymbolsGoToCase(this.currentCounterStep, Symbol.CaseWin, false);

        this.runCounterStep1();

        this.currentCounterStep++;

        this.startTimer(
            this.currentCounterStep < this.currentWins.length
                ? WinAnimatorExtended.ShowFirstWinsStep2
                : WinAnimatorExtended.ShowFirstWinsStep3,
            60,
        );
    }

    private showFirstWinsStep3(): void {
        this.allPaylinesGoToCase(Payline.CaseNowin);

        for (let i = 0; i < this.currentWins.length; i++) {
            this.winPaylineGoToCase(i, Payline.CaseNormal);
        }

        this.runCounterStep2();
        const delay = this.currentMultiplierIndex == 0 ? 45 : 90;
        let isPortrait = Scaling.isPortrait();
        if (isPortrait) {
            this._frame.goToCase("NORMAL");
        }

        this.startTimer(WinAnimatorExtended.ShowFirstWinsStep4, delay);
        //this.showFirstWinsStep4Delay.start(delay);

        this.emit(WinAnimator.FinishWinCalculation);
    }

    private showFirstWinsStep4(): void {
        this.currentCounterStep = null;
        this.runCounterStep3();

        this.startTimer(WinAnimatorExtended.ShowFirstWinsStep5, 40);
    }

    private showFirstWinsStep5(): void {
        let currentCase = this._frame.getCurrentCase();
        let isPortrait = Scaling.isPortrait();
        if (currentCase === "NORMAL" && isPortrait) {
            this._frame.goToCase("WIN");
        }
        this.finishFirstWins();
    }

    private showFirstWinsStepFS(): void {
        this.emit(WinAnimator.FinishWinAnimator);
    }

    protected winPaylineGoToCase(winIndex: number, caseType: string): void {
        if (this.paylines != null) {
            const win = this.currentWins[winIndex];

            if (win.trigger === Communication.Game.WinTrigger.Payline) {
                if (caseType === Payline.CaseCycle || caseType === Payline.CaseWin) {
                    this.paylines.goToCase(caseType, win.paylineId);
                } else {
                    this.paylines.goToCase(caseType, win.paylineId);
                }
            }
        }
    }

    //******************************************************************************************************************
    //  COUNTER
    //******************************************************************************************************************

    private resetCounter(): void {
        Tweens.removeTweens(this.counterGlow, "all");
        Tweens.removeTweens(this.counter, "all");

        this.counterGlow.alpha = 0;
        this.counterGlow.scale.x = 0.2;
        this.counterGlow.scale.y = 0.2;

        this.counter.alpha = 0;
    }

    private runCounterStep1(): void {
        let amountFrom = "0";
        let amountTo = "0";
        let currentWin: Communication.Game.IWin = null;
        console.log("runCounter1");
        for (let i = 0; i <= this.currentCounterStep; i++) {
            const win = this.currentWins[i];
            currentWin = win;

            if (win.type === Communication.Game.WinType.Cash) {
                if (i < this.currentCounterStep) {
                    amountFrom = NumberUtils.add(amountFrom, this.currentWins[i].amount);
                }

                amountTo = NumberUtils.add(amountTo, this.currentWins[i].amount);
            }
        }

        if (amountTo === "0") {
            return;
        }

        const step = (this.counterBaseSize - 0.5) / this.currentWins.length;
        const tweenDuration = this.currentMultiplierIndex == 0 ? 10 : 25;
        const counterDuration = this.currentMultiplierIndex == 0 ? 0 : 1;

        if (
            currentWin != null &&
            currentWin.symbols == null &&
            currentWin.trigger == Communication.Game.WinTrigger.Scatter &&
            currentWin.type == Communication.Game.WinType.Cash
        ) {
            PlayList.play("SND_BaseWinScatter");
        } else {
            PlayList.play(
                "SND_BaseWin0" +
                    ((this.currentMultiplierIndex >= 0 && this.currentMultiplierIndex <= 2
                        ? this.currentMultiplierIndex
                        : 2) +
                        1),
            );
        }

        Tweens.removeTweens(this.counter, "all");
        Tweens.positionTween(this.counter, {
            tweenX: [this.counter.x, this.counter.x],
            tweenY: [this.counter.y, this.counter.y],
            tweenDuration: tweenDuration,
            callbackFunction: (): void => {
                PlayList.play("SND_BaseCreditboxLoop");

                if (AppContext.gameConfiguration.UseCoins && BetManager.CoinSize != null) {
                    ImageTextCounterElement.Round = true;
                    this.counter.start(
                        parseInt(NumberUtils.devide(amountFrom, BetManager.CoinSize).toString()),
                        parseInt(NumberUtils.devide(amountTo, BetManager.CoinSize).toString()),
                        20 * counterDuration,
                    );
                } else {
                    ImageTextCounterElement.Round = false;
                    this.counter.start(parseFloat(amountFrom), parseFloat(amountTo), 20 * counterDuration);
                }
            },
        });
        Tweens.alphaTween(this.counter, {
            alphas: [this.counter.alpha, 1],
            tweenDuration: tweenDuration,
            tweenDelay: tweenDuration,
        });
        Tweens.scaleTween(this.counter, {
            tweenValues: [
                { x: 0.5 + step * this.currentCounterStep, y: 0.5 + step * this.currentCounterStep } as PIXI.Point,
                {
                    x: 0.5 + step * (this.currentCounterStep + 1),
                    y: 0.5 + step * (this.currentCounterStep + 1),
                } as PIXI.Point,
            ],
            tweenDuration: tweenDuration,
            tweenDelay: tweenDuration,
            tweenEase: "backInOut",
            callbackFunction: (): void => {
                PlayList.stop("SND_BaseCreditboxLoop");
                PlayList.play("SND_BaseCreditboxEnd");
            },
        });

        // GLOW
        if (this.counterGlow) {
            Tweens.removeTweens(this.counterGlow, "all");
            Tweens.alphaTween(this.counterGlow, {
                alphas: [this.counterGlow.alpha, this.counterGlowMaxAlpha],
                tweenDuration: tweenDuration,
                tweenDelay: tweenDuration,
            });
            Tweens.scaleTween(this.counterGlow, {
                tweenValues: [
                    {
                        x: this.counterGlow.scale.x,
                        y: this.counterGlow.scale.y,
                    } as PIXI.Point,
                    {
                        x: this.counterGlowBaseSize,
                        y: this.counterGlowBaseSize,
                    } as PIXI.Point,
                ],
                tweenDuration: tweenDuration,
                tweenDelay: tweenDuration,
            });
        }
    }

    private runCounterStep2(): void {
        const tweenDuration = this.currentMultiplierIndex == 0 ? 1 : 30;
        console.log("runCounter2");
        if (tweenDuration > 1) {
            Tweens.removeTweens(this.counter, "all");
            Tweens.alphaTween(this.counter, {
                alphas: [1, 1],
                tweenDuration: tweenDuration,
                callbackFunction: (): void => {
                    PlayList.play("SND_BaseCreditboxPop");
                },
            });
        } else {
            PlayList.play("SND_BaseCreditboxPop");
        }

        Tweens.scaleTween(this.counter, {
            tweenValues: [
                { x: this.counterBaseSize, y: this.counterBaseSize } as PIXI.Point,
                { x: this.counterBaseSize + 0.15, y: this.counterBaseSize + 0.15 } as PIXI.Point,
                { x: this.counterBaseSize, y: this.counterBaseSize } as PIXI.Point,
            ],
            tweenDuration: 15,
            tweenDelay: tweenDuration,
        });
    }

    private runCounterStep3(): void {
        PlayList.play("SND_BaseWinAway");
        console.log("runCounter3");
        Tweens.removeTweens(this.counter, "all");
        Tweens.alphaTween(this.counter, {
            alphas: [1, 0],
            tweenDuration: 30,
        });
        Tweens.scaleTween(this.counter, {
            tweenValues: [
                { x: this.counterBaseSize, y: this.counterBaseSize } as PIXI.Point,
                { x: this.counterBaseSize * 2, y: this.counterBaseSize * 2 } as PIXI.Point,
            ],
            tweenDuration: 30,
        });

        // GLOW
        if (this.counterGlow) {
            Tweens.removeTweens(this.counterGlow, "all");
            Tweens.alphaTween(this.counterGlow, {
                alphas: [this.counterGlow.alpha, 0],
                tweenDuration: 30,
            });
            Tweens.scaleTween(this.counterGlow, {
                tweenValues: [
                    {
                        x: this.counterGlowBaseSize,
                        y: this.counterGlowBaseSize,
                    } as PIXI.Point,
                    {
                        x: this.counterGlowBaseSize * 2,
                        y: this.counterGlowBaseSize * 2,
                    } as PIXI.Point,
                ],
                tweenDuration: 30,
            });
        }
    }

    private runCounterOutro(): void {
        PlayList.stop("SND_BaseCreditboxLoop");
        PlayList.stop("SND_BaseCreditboxEnd");
        PlayList.play("SND_BaseWinAway");
        console.log("runCounterOutro enter");
        if (AppContext.gameConfiguration.UseCoins && BetManager.CoinSize != null) {
            ImageTextCounterElement.Round = true;
            this.counter.start(0, parseInt(NumberUtils.devide(this.currentWin, BetManager.CoinSize).toString()), 0);
        } else {
            ImageTextCounterElement.Round = false;
            this.counter.start(0, this.currentWin, 0);
        }

        const tweenDuration = this.currentMultiplierIndex == 0 ? 10 : 20;

        Tweens.removeTweens(this.counter, "all");
        Tweens.alphaTween(this.counter, {
            alphas: [1, 0],
            tweenDuration: tweenDuration,
            tweenDelay: tweenDuration,
        });
        Tweens.scaleTween(this.counter, {
            tweenValues: [
                { x: this.counterBaseSize, y: this.counterBaseSize } as PIXI.Point,
                { x: this.counterBaseSize * 2, y: this.counterBaseSize * 2 } as PIXI.Point,
            ],
            tweenDuration: tweenDuration,
            tweenDelay: tweenDuration,
        });

        // GLOW
        if (this.counterGlow) {
            Tweens.removeTweens(this.counterGlow, "all");
            Tweens.alphaTween(this.counterGlow, {
                alphas: [this.counterGlow.alpha, 0],
                tweenDuration: tweenDuration,
                tweenDelay: tweenDuration,
            });
            Tweens.scaleTween(this.counterGlow, {
                tweenValues: [
                    {
                        x: this.counterGlowBaseSize,
                        y: this.counterGlowBaseSize,
                    } as PIXI.Point,
                    {
                        x: this.counterGlowBaseSize * 2,
                        y: this.counterGlowBaseSize * 2,
                    } as PIXI.Point,
                ],
                tweenDuration: tweenDuration,
                tweenDelay: tweenDuration,
            });
        }
    }

    public showSymbols(reelsData: Array<Communication.Game.IReel>): void {
        super.showSymbols(reelsData);
        this.sortSymbols();
    }

    protected allSymbolsGoToCase(caseType: string, noWin: boolean): void {
        for (let i = 0; i < this.currentSymbols.length; i++) {
            for (let j = this.currentSymbols[i].length - 1; j >= 0; j--) {
                const symbol = this.currentSymbols[i][j];

                if (symbol !== null && caseType == "") {
                    if (noWin) {
                        this.noWinSymbolsContainer.addChild(symbol);
                    } else {
                        this.symbolsContainer.addChild(symbol);
                    }

                    continue;
                }

                if (symbol !== null && this.allowSymbolGoToCase(i, j, caseType)) {
                    symbol.goToCase(caseType);

                    if (noWin) {
                        this.noWinSymbolsContainer.addChild(symbol);
                    } else {
                        this.symbolsContainer.addChild(symbol);
                    }
                }
            }
        }
        // this.sortSymbols();
    }

    public sortSymbols(): void {
        if (!this.symbols.hasZindex) {
            return;
        }
        let addQueue: Symbol[] = [];
        this.symbolsContainer.children.forEach((symbol) => {
            addQueue.push(<Symbol>symbol);
        });
        addQueue.sort((a, b) => a.zIndex - b.zIndex);
        addQueue.forEach((symbol) => {
            this.symbolsContainer.addChild(symbol);
        });
        addQueue = [];
        this.noWinSymbolsContainer.children.forEach((symbol) => {
            addQueue.push(<Symbol>symbol);
        });
        addQueue.sort((a, b) => a.zIndex - b.zIndex);
        addQueue.forEach((symbol) => {
            this.noWinSymbolsContainer.addChild(symbol);
        });
    }

    //******************************************************************************************************************
    //  RESET
    //******************************************************************************************************************

    // public reset(showLastSymbols: boolean, spin = false): boolean {
    //     this.stopTimer();
    //
    //     this._scattertCount = 0;
    //
    //     this.allSymbolsGoToCase(showLastSymbols ? Symbol.CaseNormal : Symbol.CaseEmpty, false);
    //
    //     this.allPaylinesGoToCase(Payline.CaseNormal);
    //
    //     if (showLastSymbols && !spin) {
    //         this.startTimer(WinAnimator.Idle, this.getInitialIdleDelay());
    //     }
    //
    //     if (this.getActiveElementCase() === WinAnimatorExtended.CaseWin) {
    //         this.setActiveElementCase(WinAnimatorExtended.CaseHide);
    //     }
    //
    //     this.value.visible = false;
    //
    //     let lastMultiplierIndex = this.currentMultiplierIndex;
    //     this.currentMultiplierIndex = -1;
    //
    //     if (this.currentAnimationIndex != null) {
    //         if (this.currentAnimationFinished) {
    //             this.resetAnimations();
    //
    //             this.currentWins = null;
    //             this.currentWin = null;
    //             return false;
    //         } else {
    //             this.stopAnimation();
    //
    //             this.currentWins = null;
    //             this.currentWin = null;
    //             return (lastMultiplierIndex >= 2); // condition should be greater than or equals 2 beacause we`ve got only two types of base wins (before high wins)
    //         }
    //     } else {
    //
    //         this.currentWins = null;
    //         this.currentWin = null;
    //         return this._wasHighWin;
    //     }
    // }

    reset(showLastSymbols: boolean, spin: boolean = false): boolean {
        this._scattertCount = 0;
        this.stopLogoTimer();
        if (showLastSymbols && !spin) {
            let rnd = this.getRandom(120, this._idleMaxValue);
            this.startLogoTimer(WinAnimatorExtended.IdleLogo, rnd);
        }
        if (this.currentCounterStep != null) {
            this.currentCounterStep = null;

            this.runCounterOutro();
        }

        return super.reset(showLastSymbols, spin);
    }

    //******************************************************************************************************************
    //  ACTIVE ELEMENTS
    //******************************************************************************************************************

    /**
     * Attempts to switch all Active Elements to the earliest case from array the element can be switched to.
     * Elements with no case from the list are ignored.
     * @param s Array of names of cases to try to switch to
     * @param emitEvent
     * @param delay Switch delay
     */
    private setActiveElementCaseArray(s: Array<string>, emitEvent: boolean = false, delay?: number) {
        if (!s.length) {
            return;
        }
        if (!this.visible) {
            return;
        }
        this._activeElementsCase = s[0];
        this._activeElements.forEach((element) => {
            for (let i = 0; i < s.length; i++) {
                if (element.hasCase(s[i])) {
                    element.goToCase(s[i], emitEvent, delay);
                    break;
                }
            }
        });
    }

    /**
     * Attempts to switch all Active Elements to the given case if they have this case.
     * Elements without the case are ignored.
     * @param s Case name
     * @param emitEvent
     * @param delay
     */
    private setActiveElementCase(s: string, emitEvent: boolean = false, delay?: number) {
        if (!this.visible) {
            return;
        }
        this._activeElementsCase = s;
        this._activeElements.forEach((element) => {
            if (element.hasCase(s)) {
                element.goToCase(s, emitEvent, delay);
            }
        });
    }

    /**
     * Returns last case switched to, or the first case from a list switched to.
     */
    private getActiveElementCase(): string {
        return this._activeElementsCase;
    }

    protected startAnimation() {
        super.startAnimation();
        if (this.currentWin === 0) {
            const animation = this.animations[this.currentAnimationIndex];
            animation.visible = false;
        }
        if (this.currentAnimationIndex > 0 && this.currentAnimationIndex <= 2) {
            this._logo.goToCase("PLAY");
        }
        if (this.currentWin === 0) {
            const animation = this.animations[this.currentAnimationIndex];
            if (animation != null) {
                animation.visible = false;

                if (
                    this.currentWins &&
                    this.currentWins.filter((win) => win.trigger === Communication.Game.WinTrigger.Scatter).length
                ) {
                    animation.stop();
                    this.finishFirstWins();
                }
            }
        }
    }

    public IsInHighWin(): boolean {
        return this.currentAnimationIndex != null && this.currentAnimationIndex > 2;
    }

    protected stopAnimation(): void {
        // if(this.currentAnimationIndex != null && this.currentAnimationIndex >= 3)
        // {
        //     //this._logo.goToCase("NORMAL");
        //     if (Scaling.isPortrait())
        //     {
        //         this._frame.goToCase("WIN");
        //     }
        // }
        super.stopAnimation();
    }

    private getRandom(min: number, max: number): number {
        return Math.floor(Math.random() * (max - min) + min);
    }

    private idleLogo(): void {
        if (this._logo.getCurrentCase() === Symbol.CaseNormal) {
            if (this._logo.hasCase("PLAY")) {
                this._logo.goToCase("PLAY");
            }
        }

        let rnd = this.getRandom(120, this._idleMaxValue);
        this.startLogoTimer(WinAnimatorExtended.IdleLogo, rnd);
    }

    protected idle(): void {
        for (let i = 0; i < this.getIdleActiveElementsCount(); i++) {
            const symbol =
                this.currentSymbols[Math.floor(Math.random() * this.reelCount)][
                    Math.floor(Math.random() * this.symbolCount)
                ];

            if (symbol.id + 1 != Settings.SymbolBonus && symbol.getCurrentCase() === Symbol.CaseNormal) {
                if (symbol.hasCase(Symbol.CaseIdle)) {
                    symbol.goToCase(Symbol.CaseIdle);
                }
            }
        }

        this.startTimer(WinAnimator.Idle, this.getIdleDelay());
    }
}

export const WinAnimatorExtendedAttributes = {
    ACTIVE_ELEMENTS: "ActiveElements",
};



/** WEBPACK FOOTER **
 ** ./Engine/Game/CustomComponents/WinAnimatorExtended.ts
 **/